/**
 * useUpdateTipsRetention Hook (V3)
 *
 * React Query mutation hook for updating tips retention in Personnel domain.
 * Handles the two-step process with ParrotAPI and Personnel API.
 * Uses i18n for toast notifications with 'personnel' namespace.
 *
 * @module domains/personnel/presentation/hooks/useUpdateTipsRetention
 */

import { useMutation, useQueryClient } from 'react-query';
import { toast } from '@parrot-software/design-system';
import { useTranslation } from 'react-i18next';

import { getTipsUseCases } from '@personnel/composition';
import type { UpdateTipsRetentionParamsV3 } from '@personnel/application/dtos/TipsRetentionDto';
import { ROLES_QUERY_KEYS } from './useRoleList';

/**
 * Hook options for configuring update behavior
 */
export interface UseUpdateTipsRetentionOptions {
  /** Callback executed on successful update */
  onSuccess?: () => void;
  /** Callback executed on error */
  onError?: (error: Error) => void;
}

/**
 * Hook result with mutation function and states
 */
export interface UseUpdateTipsRetentionResult {
  /** Mutation function to update tips retention */
  updateTipsRetention: (params: UpdateTipsRetentionParamsV3) => Promise<void>;
  /** Whether the update is in progress */
  isLoading: boolean;
  /** Whether the update was successful */
  isSuccess: boolean;
  /** Whether the update failed */
  isError: boolean;
  /** Error object if update failed */
  error: Error | null;
  /** Reset mutation state */
  reset: () => void;
}

/**
 * React Query mutation hook for updating tips retention (V3)
 *
 * Features:
 * - Two-step API update (ParrotAPI + Personnel API)
 * - Automatic cache invalidation
 * - Success/error toast notifications
 * - Loading and error states
 * - TypeScript type safety
 *
 * @param options - Hook configuration options
 * @returns Mutation object with updateTipsRetention function and states
 *
 * @example
 * const { updateTipsRetention, isLoading } = useUpdateTipsRetention({
 *   onSuccess: () => console.log('Updated successfully'),
 * });
 *
 * await updateTipsRetention({
 *   storeUuid: 'store-123',
 *   organizationUuid: 'org-456',
 *   tipRetentionType: 'NO_RETENTION',
 *   tipRetentionAmount: 3,
 *   roleRetentions: [
 *     { roleUuid: 'role-789', tipRetentionAmount: '5.00' },
 *     { roleUuid: 'role-456', tipRetentionAmount: null }
 *   ]
 * });
 */
export const useUpdateTipsRetention = (options: UseUpdateTipsRetentionOptions = {}): UseUpdateTipsRetentionResult => {
  const { onSuccess, onError } = options;

  const { t } = useTranslation('personnel');
  const queryClient = useQueryClient();
  const { updateTipsRetention: updateTipsRetentionUseCase } = getTipsUseCases();

  const mutation = useMutation({
    mutationFn: async (params: UpdateTipsRetentionParamsV3) => {
      await updateTipsRetentionUseCase.execute(params);
    },
    onSuccess: () => {
      // Invalidate roles list to refresh tipRetentionAmount values
      queryClient.invalidateQueries(ROLES_QUERY_KEYS.list());

      // Invalidate ParrotAPI store retention query (Step 1 endpoint)
      queryClient.invalidateQueries('retentions-global');

      // Invalidate legacy settings query (for V1 compatibility if needed)
      queryClient.invalidateQueries('settings-tips');

      // Show success toast
      toast.success(t('tipsRetention.success'), {
        duration: 3000,
      });

      // Execute custom callback
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error: Error) => {
      // Show error toast
      toast.error(t('tipsRetention.error'), {
        duration: 6000,
      });

      // Execute custom callback
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    updateTipsRetention: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error || null,
    reset: mutation.reset,
  };
};
